import { useRef, useState, type MouseEvent, type ReactElement } from 'react';

import { ModalShell } from '@/components/ui/ModalShell';
import { useToast } from '@/components/ui/toast';
import { useActiveCompany } from '@/lib/active-company-context';
import { apiErrorReason, ApiError } from '@/lib/api';
import { plural } from '@/lib/plural';

import type { ParsedSheet } from '../parse-sheet';
import { importError, serverProblems } from '../presentation';
import { readImportFile } from '../read-file';
import { downloadTemplate } from '../template';
import { importPlanSchema, type CommitResult, type ImportPlan } from '../types';
import { useCommitImport, useValidateImport } from '../use-migration';
import { ImportDone } from './ImportDone';
import { ImportFileStep } from './ImportFileStep';
import { ImportPreview } from './ImportPreview';

// "Importar desde Excel" (spec 2026-10-14 §2): migración de la Lista Maestra desde el
// OneDrive de Calidad, dentro de la Lista Maestra (sin página nueva). Archivo →
// previsualización del servidor (nada se escribe) → confirmación explícita → resultado.

type Step = 'file' | 'preview' | 'done';

/** Plan nuevo que el servidor devuelve si al confirmar aparecieron errores. */
function freshPlan(err: unknown): ImportPlan | null {
  if (apiErrorReason(err) !== 'import_has_errors' || !(err instanceof ApiError)) {
    return null;
  }
  const parsed = importPlanSchema.safeParse(err.meta?.['plan']);
  return parsed.success ? parsed.data : null;
}

export function ImportModal({ onClose }: { onClose: () => void }): ReactElement {
  const { activeCompanyName } = useActiveCompany();
  const validate = useValidateImport();
  const commit = useCommitImport();
  const { showToast } = useToast();
  const [step, setStep] = useState<Step>('file');
  const [source, setSource] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ParsedSheet | null>(null);
  const [reading, setReading] = useState(false);
  const [plan, setPlan] = useState<ImportPlan | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<CommitResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Cada lectura lleva su número: una que termina tarde no pisa al archivo elegido después.
  const readSeq = useRef(0);
  const busy = reading || validate.isPending || commit.isPending;
  const body = { source: source.trim(), rows: parsed?.rows ?? [] };
  const rowNumbers = parsed?.rowNumbers ?? [];
  const canPreview =
    parsed !== null && parsed.problems.length === 0 && body.rows.length > 0 && body.source !== '';
  const canImport = plan !== null && plan.summary.errors === 0 && plan.summary.entriesToCreate > 0;

  async function chooseFile(f: File): Promise<void> {
    const seq = ++readSeq.current;
    setReading(true);
    setError(null);
    setFileName(f.name);
    setParsed(null);
    try {
      const sheet = await readImportFile(f);
      if (seq === readSeq.current) {
        setParsed(sheet);
      }
    } catch {
      if (seq === readSeq.current) {
        setError('No se pudo leer el archivo. Revisa que sea un Excel (.xlsx) y súbelo de nuevo.');
      }
    } finally {
      if (seq === readSeq.current) {
        setReading(false);
      }
    }
  }

  async function preview(): Promise<void> {
    setError(null);
    try {
      setPlan(await validate.mutateAsync(body));
      setConfirming(false);
      setStep('preview');
    } catch (err) {
      const problems = serverProblems(err, body.rows, rowNumbers);
      if (problems.length > 0 && parsed !== null) {
        setParsed({ ...parsed, problems });
      } else {
        setError(importError(err));
      }
    }
  }

  async function confirmImport(e: MouseEvent): Promise<void> {
    // El segundo clic de un doble clic (detail 2) no confirma: la confirmación se lee.
    if (e.detail > 1) {
      return;
    }
    setError(null);
    try {
      const done = await commit.mutateAsync(body);
      setResult(done);
      const n = done.counts.entriesCreated;
      showToast(
        done.replayed
          ? 'Este archivo ya se había importado: no se creó nada nuevo'
          : `${plural(n, 'documento importado', 'documentos importados')} a la Lista Maestra`,
      );
      setStep('done');
    } catch (err) {
      setConfirming(false);
      const fresh = freshPlan(err);
      if (fresh !== null) {
        setPlan(fresh);
        setError(
          'Algunos documentos cambiaron mientras revisabas y ahora tienen errores: se muestran arriba.',
        );
        return;
      }
      setError(importError(err));
    }
  }

  const n = plan?.summary.entriesToCreate ?? 0;
  const footer =
    step === 'file' ? (
      <>
        <button type="button" className="btn mut sm" onClick={onClose} disabled={busy}>
          Cancelar
        </button>
        <button
          type="button"
          className="btn sm"
          disabled={!canPreview || busy}
          onClick={() => void preview()}
        >
          {validate.isPending ? 'Previsualizando…' : 'Previsualizar'}
        </button>
      </>
    ) : step === 'preview' ? (
      <>
        <button
          type="button"
          className="btn mut sm"
          disabled={busy}
          onClick={() => (confirming ? setConfirming(false) : setStep('file'))}
        >
          {confirming ? 'Cancelar' : 'Volver al archivo'}
        </button>
        {canImport && (
          <button
            type="button"
            className="btn sm"
            disabled={busy}
            onClick={(e) => (confirming ? void confirmImport(e) : setConfirming(true))}
          >
            {commit.isPending
              ? 'Importando…'
              : confirming
                ? 'Sí, importar'
                : `Importar ${plural(n, 'documento', 'documentos')}`}
          </button>
        )}
      </>
    ) : (
      <button type="button" className="btn sm" onClick={onClose}>
        Ver la Lista Maestra
      </button>
    );

  return (
    <ModalShell
      open
      title="Importar desde Excel"
      wide
      busy={busy}
      onClose={onClose}
      footer={footer}
    >
      {step === 'file' && (
        <ImportFileStep
          source={source}
          onSource={setSource}
          parsed={parsed}
          fileName={fileName}
          reading={reading}
          onFile={(f) => void chooseFile(f)}
          onDownload={() => void downloadTemplate()}
        />
      )}
      {step === 'preview' && plan !== null && <ImportPreview plan={plan} rowNumbers={rowNumbers} />}
      {step === 'preview' && confirming && (
        <p className="import-confirm" role="alert">
          {`Se crearán ${plural(n, 'documento', 'documentos')} en la Lista Maestra de ${activeCompanyName ?? 'la empresa activa'}. Quedan registrados como carga histórica.`}
        </p>
      )}
      {step === 'done' && result !== null && <ImportDone result={result} rowNumbers={rowNumbers} />}
      {error && (
        <p className="fld-error" role="alert">
          {error}
        </p>
      )}
    </ModalShell>
  );
}
